import React, { useEffect, useMemo, useState } from "react";
import { userAuthHeader } from "../_helpers";
import { useSelector } from "react-redux";
import axios from "axios";
import moment from "moment";
import Layout from "./Layout";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Snackbar,
  Alert as MuiAlert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  InputAdornment,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import { ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";

// closingDate is varchar(10) stored as YYYY-MM-DD in deposits_draftfilings;
// older rows / legacy code may pass DD/MM/YYYY. Accept both.
const parseDate = (value) => {
  if (!value) return null;
  const m = moment(value, ["YYYY-MM-DD", "DD/MM/YYYY", moment.ISO_8601], true);
  return m.isValid() ? m : null;
};
const formatDate = (value, fmt = "DD/MM/YYYY") => {
  const m = parseDate(value);
  return m ? m.format(fmt) : "N/A";
};
const formatDateTime = (value) => {
  if (!value) return "";
  const m = moment(value);
  return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : "";
};

// French formatting: 129,38 € (NBSP before €) — Intl handles locale rules
const EUR = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" });
const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "N/A";
  const n = parseFloat(v);
  return isNaN(n) ? "N/A" : EUR.format(n);
};
// SIREN: 9 digits displayed as "XXX XXX XXX"
const fmtSiren = (v) => {
  if (!v) return "N/A";
  const s = String(v).replace(/\D/g, "");
  return s.length === 9 ? `${s.slice(0, 3)} ${s.slice(3, 6)} ${s.slice(6)}` : s;
};

const STEP_LABELS = {
  company: "Sélection de l'entreprise",
  documents: "Documents",
  pricing: "Tarification",
  payment: "Paiement",
  review: "Révision",
};
const getStepLabel = (step) => STEP_LABELS[step] || step || "—";

// Brand button styles — match the BusyPlace green used elsewhere in the dashboard
const BRAND = "#1cbe93";
const BRAND_HOVER = "#17a37e";
const greenContained = {
  backgroundColor: BRAND,
  color: "white",
  "&:hover": { backgroundColor: BRAND_HOVER },
};
const greenOutlined = {
  borderColor: BRAND,
  color: BRAND,
  "&:hover": { borderColor: BRAND_HOVER, backgroundColor: "rgba(28,190,147,0.04)" },
};
const greenText = {
  color: BRAND,
  "&:hover": { backgroundColor: "rgba(28,190,147,0.04)" },
};

export default function DepotComptes() {
  const navigate = useNavigate();
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalFilings, setTotalFilings] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const [draftFilings, setDraftFilings] = useState([]);
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftPage, setDraftPage] = useState(0);
  const [draftRowsPerPage, setDraftRowsPerPage] = useState(10);
  const [totalDrafts, setTotalDrafts] = useState(0);

  // Selection + bulk-delete state
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirm, setConfirm] = useState({ open: false, mode: null, target: null });
  const [isBusy, setIsBusy] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });
  // 'grid' | 'list' — user preference, persisted across sessions
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("draftsViewMode") || "grid");
  const handleViewModeChange = (_e, next) => {
    if (next) {
      setViewMode(next);
      localStorage.setItem("draftsViewMode", next);
    }
  };

  const currentUserData = useSelector((state) => state.authentication.user);
  const [currentUser, setCurrentUser] = useState();
  useEffect(() => {
    if (currentUserData) setCurrentUser(currentUserData?.data || currentUserData);
  }, [currentUserData]);

  useEffect(() => {
    fetchFilings();
    fetchDraftFilings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, draftPage, draftRowsPerPage, searchTerm]);

  // Stale-selection cleanup: drop ids that are no longer in the current page
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(draftFilings.map((d) => d.id));
      const next = new Set();
      prev.forEach((id) => visible.has(id) && next.add(id));
      return next.size === prev.size ? prev : next;
    });
  }, [draftFilings]);

  const fetchFilings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}api/user/deposit-accounts`,
        {
          params: { page: page + 1, limit: rowsPerPage, search: searchTerm },
          headers: userAuthHeader(),
        }
      );
      if (response.data && response.data.success) {
        setFilings(response.data.data || []);
        setTotalFilings(response.data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching deposit accounts:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDraftFilings = async () => {
    setDraftLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}api/user/draft-filings`,
        {
          params: { page: draftPage + 1, limit: draftRowsPerPage, search: searchTerm },
          headers: userAuthHeader(),
        }
      );
      if (response.data && response.data.success) {
        setDraftFilings(response.data.data || []);
        setTotalDrafts(response.data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching draft filings:", error);
    } finally {
      setDraftLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleDraftChangePage = (event, newPage) => setDraftPage(newPage);
  const handleDraftChangeRowsPerPage = (event) => {
    setDraftRowsPerPage(parseInt(event.target.value, 10));
    setDraftPage(0);
  };
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
    setDraftPage(0);
  };

  const getStatusColor = (s) => ({ draft: "default", signed: "success", submitted: "warning", validated: "success", rejected: "error", pending: "warning" }[s] || "default");
  const getStatusLabel = (s) => ({ draft: "Brouillon", signed: "Signé", submitted: "Soumis", validated: "Validé", rejected: "Rejeté", pending: "En attente" }[s] || s);

  const handleViewDetails = (id) => navigate(`/prouser/depot-comptes/${id}`);
  const handleContinueDraft = (draftId) => {
    const baseUrl = `${process.env.REACT_APP_DEPOSITE_URL}?id=${currentUser?.token || currentUser?.data?.token}`;
    window.open(`${baseUrl}&draft=${draftId}`, "_blank", "noopener,noreferrer");
  };
  const handleEditRejectedFiling = (filingId) => {
    const baseUrl = `${process.env.REACT_APP_DEPOSITE_URL}?id=${currentUser?.token || currentUser?.data?.token}`;
    window.open(`${baseUrl}&editFiling=${filingId}`, "_blank", "noopener,noreferrer");
  };

  // --- selection helpers ---
  const visibleIds = useMemo(() => draftFilings.map((d) => d.id), [draftFilings]);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someSelected = !allSelected && visibleIds.some((id) => selectedIds.has(id));

  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(visibleIds));
  };
  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const askDeleteSingle = (id) => setConfirm({ open: true, mode: "single", target: id });
  const askDeleteBulk = () => setConfirm({ open: true, mode: "bulk", target: null });
  const askDeleteAll = () => setConfirm({ open: true, mode: "all", target: null });
  const closeConfirm = () => {
    if (isBusy) return;
    setConfirm({ open: false, mode: null, target: null });
  };

  const performDelete = async () => {
    if (!confirm.mode) return;
    setIsBusy(true);
    try {
      // Filing delete: a single submitted Dépôt — guarded server-side; INPI
      // cancellation is best-effort and reported in the snack.
      if (confirm.mode === "filing") {
        try {
          const resp = await axios.delete(
            `${process.env.REACT_APP_API_URL}api/user/deposit-accounts/${confirm.target}`,
            { headers: userAuthHeader() }
          );
          const inpi = resp.data?.inpi;
          const inpiSkipped = inpi?.reason === "no_inpi_reference" || inpi?.reason === "not_on_inpi";
          setSnack({
            open: true,
            severity: "success",
            message: inpiSkipped
              ? "Dépôt supprimé (aucune action INPI nécessaire)"
              : "Dépôt supprimé (INPI inclus)",
          });
          setConfirm({ open: false, mode: null, target: null });
          await fetchFilings();
        } catch (err) {
          const data = err.response?.data;
          let message;
          if (data?.code === "SIGNED_ON_INPI") {
            message =
              "Ce dépôt a déjà été signé sur le Guichet Unique INPI. Veuillez contacter un administrateur pour le supprimer.";
          } else if (data?.code === "INPI_VERIFICATION_FAILED") {
            message =
              "Impossible de vérifier le statut du dépôt sur l'INPI. Veuillez réessayer plus tard ou contacter un administrateur.";
          } else if (data?.code === "STATUS_NOT_DELETABLE") {
            message = "Suppression impossible : seul un dépôt « Soumis » peut être supprimé.";
          } else {
            message = data?.message || "Erreur lors de la suppression du dépôt";
          }
          setSnack({ open: true, severity: "error", message });
        }
        return;
      }

      let ids = [];
      if (confirm.mode === "single") ids = [confirm.target];
      else if (confirm.mode === "bulk") ids = Array.from(selectedIds);
      else if (confirm.mode === "all") {
        const limit = Math.max(totalDrafts, 200);
        const resp = await axios.get(`${process.env.REACT_APP_API_URL}api/user/draft-filings`, {
          params: { page: 1, limit, search: searchTerm },
          headers: userAuthHeader(),
        });
        ids = (resp.data?.data || []).map((d) => d.id);
      }

      let deleted = 0;
      const chunkSize = 6;
      for (let i = 0; i < ids.length; i += chunkSize) {
        const chunk = ids.slice(i, i + chunkSize);
        // eslint-disable-next-line no-await-in-loop
        const results = await Promise.allSettled(
          chunk.map((id) =>
            axios.delete(`${process.env.REACT_APP_API_URL}api/user/draft-filings/${id}`, {
              headers: userAuthHeader(),
            })
          )
        );
        deleted += results.filter((r) => r.status === "fulfilled" && r.value?.data?.success).length;
      }

      setSnack({
        open: true,
        severity: deleted === ids.length ? "success" : "warning",
        message:
          deleted === 0
            ? "Aucun brouillon supprimé"
            : `${deleted} brouillon${deleted > 1 ? "s" : ""} supprimé${deleted > 1 ? "s" : ""}`,
      });
      clearSelection();
      setConfirm({ open: false, mode: null, target: null });
      await fetchDraftFilings();
    } catch (e) {
      console.error("Bulk delete failed:", e);
      setSnack({ open: true, severity: "error", message: "Erreur lors de la suppression" });
    } finally {
      setIsBusy(false);
    }
  };

  const askDeleteFiling = (id) => setConfirm({ open: true, mode: "filing", target: id });

  const confirmCopy = (() => {
    if (confirm.mode === "single") return { title: "Supprimer ce brouillon ?", body: "Cette action est définitive." };
    if (confirm.mode === "bulk") {
      const n = selectedIds.size;
      return { title: `Supprimer ${n} brouillon${n > 1 ? "s" : ""} ?`, body: "Les brouillons sélectionnés seront définitivement supprimés." };
    }
    if (confirm.mode === "all") {
      return { title: `Supprimer tous les brouillons (${totalDrafts}) ?`, body: "Tous vos brouillons seront définitivement supprimés. Cette action ne peut pas être annulée." };
    }
    if (confirm.mode === "filing") {
      return {
        title: "Supprimer ce dépôt ?",
        body: "Le dépôt sera supprimé localement et, si possible, retiré de l'INPI. Cette action est définitive.",
      };
    }
    return { title: "", body: "" };
  })();

  return (
    <Layout>
      <div className="xl:col-span-9 col-span-12 bg-white rounded-[20px] sideShadow p-3 sm:p-4 md:p-5 lg:p-6">
        {/* Header */}
        <div className="mb-4 sm:mb-5 md:mb-6">
          <Typography variant="h4" component="h1" className="font-bold mb-2 text-xl sm:text-2xl md:text-3xl lg:text-4xl">
            Mes dépôts de comptes annuels
          </Typography>
          <Typography variant="body2" color="text.secondary" className="text-xs sm:text-sm">
            Consultez et gérez vos dépôts de comptes annuels
          </Typography>
        </div>
        <div className="mb-4 sm:mb-5 md:mb-6">
          <a href={`${process.env.REACT_APP_DEPOSITE_URL}?id=${currentUser?.token || currentUser?.data?.token}`} target="_blank" rel="noopener noreferrer">
            <Button variant="contained" className="w-full sm:w-auto sm:min-w-[200px] h-12 text-sm sm:text-base text-white bg-secondary rounded-md border-2 border-thColor transition-all duration-300 hover:border-secondary hover:text-white hover:btnShadow hover:bg-secondary py-5">
              Ajouter un dépôt de compte
            </Button>
          </a>
        </div>

        {/* Stats Cards */}
        <Grid container spacing={2} className="mb-4 sm:mb-5 md:mb-6">
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography color="text.secondary" gutterBottom>Total des dépôts</Typography>
              <Typography variant="h4">{totalFilings}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography color="text.secondary" gutterBottom>En attente</Typography>
              <Typography variant="h4">
                {filings.filter((f) => f.status === "pending" || f.status === "submitted").length}
              </Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography color="text.secondary" gutterBottom>Validés</Typography>
              <Typography variant="h4" color="success.main">
                {filings.filter((f) => f.status === "validated").length}
              </Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography color="text.secondary" gutterBottom>Brouillons</Typography>
              <Typography variant="h4">{totalDrafts}</Typography>
            </CardContent></Card>
          </Grid>
        </Grid>

        {/* Search and Actions */}
        <Box className="mb-4 flex flex-col sm:flex-row gap-2 sm:gap-4">
          <TextField
            fullWidth
            placeholder="Rechercher par SIREN ou nom..."
            value={searchTerm}
            onChange={handleSearch}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon /></InputAdornment>
              ),
            }}
            sx={{ "& .MuiInputBase-root": { fontSize: { xs: "0.875rem", sm: "1rem" } } }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { fetchFilings(); fetchDraftFilings(); }}
            disabled={loading || draftLoading}
            className="whitespace-nowrap"
            sx={{ ...greenOutlined, minWidth: { xs: "100%", sm: "auto" } }}
          >
            Actualiser
          </Button>
        </Box>

        <Box className="mb-4">
          <Tabs
            value={activeTab}
            onChange={(e, v) => { setActiveTab(v); clearSelection(); }}
            sx={{
              "& .MuiTab-root": { textTransform: "none", fontWeight: 600, color: "text.secondary" },
              "& .Mui-selected": { color: `${BRAND} !important` },
              "& .MuiTabs-indicator": { backgroundColor: BRAND },
            }}
          >
            <Tab label="Dépôts" />
            <Tab label="Brouillons" />
          </Tabs>
        </Box>

        {/* Sticky bulk-action toolbar for Brouillons tab */}
        {activeTab === 1 && draftFilings.length > 0 && (
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 5,
              backgroundColor: "background.paper",
              borderBottom: "1px solid",
              borderColor: "divider",
              py: 1.5,
              px: { xs: 1, sm: 2 },
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 2 },
              flexWrap: "wrap",
            }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {selectedIds.size} sélectionné{selectedIds.size > 1 ? "s" : ""} sur {draftFilings.length}
            </Typography>
            {selectedIds.size > 0 ? (
              <>
                <Button size="small" onClick={clearSelection} disabled={isBusy} sx={greenText}>
                  Annuler la sélection
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  startIcon={isBusy ? <CircularProgress size={14} sx={{ color: "white" }} /> : <DeleteIcon />}
                  onClick={askDeleteBulk}
                  disabled={isBusy}
                >
                  Supprimer la sélection ({selectedIds.size})
                </Button>
              </>
            ) : (
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={askDeleteAll}
                disabled={isBusy || totalDrafts === 0}
              >
                Tout supprimer ({totalDrafts})
              </Button>
            )}

            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              sx={{
                ml: "auto",
                "& .MuiToggleButton-root.Mui-selected": { color: BRAND, backgroundColor: "rgba(28,190,147,0.08)" },
              }}
            >
              <ToggleButton value="grid" aria-label="Vue grille">
                <Tooltip title="Vue grille"><ViewModuleIcon fontSize="small" /></Tooltip>
              </ToggleButton>
              <ToggleButton value="list" aria-label="Vue liste">
                <Tooltip title="Vue liste"><ViewListIcon fontSize="small" /></Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {/* Brouillons → responsive card grid (no horizontal scroll at any width) */}
        {activeTab === 1 && (
          <Box>
            {draftLoading ? (
              <Box className="py-12 text-center"><CircularProgress sx={{ color: BRAND }} /></Box>
            ) : draftFilings.length === 0 ? (
              <Box className="py-12 text-center text-gray-500 bg-white rounded-xl shadow-sm">
                Aucun brouillon trouvé
              </Box>
            ) : (
              <div className={viewMode === "list" ? "flex flex-col gap-2" : "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"}>
                {draftFilings.map((draft) => {
                  const checked = selectedIds.has(draft.id);
                  return (
                    <Card
                      key={draft.id}
                      sx={{
                        border: checked ? `2px solid ${BRAND}` : "1px solid rgba(0,0,0,0.12)",
                        boxShadow: checked ? `0 0 0 4px rgba(28,190,147,0.12)` : 1,
                        transition: "box-shadow 120ms ease, border-color 120ms ease",
                      }}
                    >
                      {viewMode === "list" ? (
                        <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, flexWrap: "nowrap" }}>
                            <Checkbox
                              color="primary"
                              checked={checked}
                              onChange={() => toggleOne(draft.id)}
                              disabled={isBusy}
                              sx={{ p: 0.5, flex: "0 0 auto" }}
                              inputProps={{ "aria-label": `Sélectionner brouillon ${draft.id}` }}
                            />
                            <Box sx={{ flex: "0 1 240px", minWidth: 0 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {draft.companyName || "Entreprise non définie"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                                SIREN {fmtSiren(draft.siren)}
                              </Typography>
                            </Box>
                            <Box sx={{ flex: "0 0 auto", display: { xs: "none", md: "block" } }}>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>Clôture</Typography>
                              <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>{formatDate(draft.closingDate)}</Typography>
                            </Box>
                            <Box sx={{ flex: "0 0 auto", display: { xs: "none", lg: "block" } }}>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>Sauvegardé</Typography>
                              <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>{formatDate(draft.lastSavedAt)}</Typography>
                            </Box>
                            <Chip
                              label={getStepLabel(draft.currentStep)}
                              size="small"
                              sx={{ flex: "0 0 auto", backgroundColor: "rgba(28,190,147,0.12)", color: BRAND_HOVER, fontWeight: 500, display: { xs: "none", sm: "inline-flex" } }}
                            />
                            <Box sx={{ flex: "0 0 auto", display: "flex", gap: 1, ml: "auto" }}>
                              <Button size="small" color="error" onClick={() => askDeleteSingle(draft.id)} disabled={isBusy} sx={{ minWidth: 0 }}>
                                <DeleteIcon fontSize="small" />
                                <Box component="span" sx={{ display: { xs: "none", md: "inline" }, ml: 0.5 }}>Supprimer</Box>
                              </Button>
                              <Button size="small" variant="contained" onClick={() => handleContinueDraft(draft.id)} disabled={isBusy} sx={greenContained}>
                                Continuer
                              </Button>
                            </Box>
                          </Box>
                        </CardContent>
                      ) : (
                        <CardContent>
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1.5 }}>
                            <Checkbox
                              color="primary"
                              checked={checked}
                              onChange={() => toggleOne(draft.id)}
                              disabled={isBusy}
                              sx={{ p: 0.5, mt: -0.5 }}
                              inputProps={{ "aria-label": `Sélectionner brouillon ${draft.id}` }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
                                {draft.companyName || "Entreprise non définie"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                SIREN {fmtSiren(draft.siren)}
                              </Typography>
                            </Box>
                          </Box>

                          <Box className="grid grid-cols-2 gap-3 mb-3">
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">Date de clôture</Typography>
                              <Typography variant="body2">{formatDate(draft.closingDate)}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">Dernière sauvegarde</Typography>
                              <Typography variant="body2">{formatDate(draft.lastSavedAt)}</Typography>
                            </Box>
                            <Box sx={{ gridColumn: "span 2" }}>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Étape</Typography>
                              <Chip
                                label={getStepLabel(draft.currentStep)}
                                size="small"
                                sx={{ backgroundColor: "rgba(28,190,147,0.12)", color: BRAND_HOVER, fontWeight: 500 }}
                              />
                            </Box>
                          </Box>

                          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", borderTop: "1px solid", borderColor: "divider", pt: 1.5 }}>
                            <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => askDeleteSingle(draft.id)} disabled={isBusy}>
                              Supprimer
                            </Button>
                            <Button size="small" variant="contained" onClick={() => handleContinueDraft(draft.id)} disabled={isBusy} sx={greenContained}>
                              Continuer
                            </Button>
                          </Box>
                        </CardContent>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}

            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
              <TablePagination
                component="div"
                count={totalDrafts}
                page={draftPage}
                onPageChange={handleDraftChangePage}
                rowsPerPage={draftRowsPerPage}
                onRowsPerPageChange={handleDraftChangeRowsPerPage}
                rowsPerPageOptions={[6, 12, 24, 48]}
                labelRowsPerPage="Par page :"
              />
            </Box>
          </Box>
        )}

        {/* Dépôts → desktop table */}
        {activeTab === 0 && (
        <Paper className="hidden md:block overflow-hidden">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Entreprise</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>SIREN</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>Date clôture</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>Montant</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>Date soumission</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} align="center"><CircularProgress /></TableCell></TableRow>
                ) : filings.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">Aucun dépôt de comptes trouvé</Typography>
                  </TableCell></TableRow>
                ) : (
                  filings.map((filing) => (
                    <TableRow key={filing.id} hover>
                      <TableCell>{filing.companyName || "N/A"}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtSiren(filing.siren)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(filing.closingDate)}</TableCell>
                      <TableCell>
                        <Chip label={getStatusLabel(filing.status)} color={getStatusColor(filing.status)} size="small" />
                        {filing.rejectionReason && (
                          <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                            {filing.rejectionReason}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtMoney(filing.amountPaid)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(filing.submittedAt) || "N/A"}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewDetails(filing.id)}
                            sx={greenOutlined}
                          >
                            Voir
                          </Button>
                          {filing.status === "rejected" && (
                            <Button variant="contained" size="small" color="error" startIcon={<EditIcon />} onClick={() => handleEditRejectedFiling(filing.id)}>
                              Modifier
                            </Button>
                          )}
                          {filing.status === "submitted" && (
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() => askDeleteFiling(filing.id)}
                              disabled={isBusy}
                            >
                              Supprimer
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalFilings}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Lignes par page:"
          />
        </Paper>
        )}

        {/* Mobile Card View (Dépôts only — Brouillons uses the responsive grid above) */}
        {activeTab === 0 && (
        <div className="block md:hidden space-y-4">
          {
            loading ? (
              <div className="py-12 text-center"><CircularProgress /></div>
            ) : filings.length === 0 ? (
              <div className="py-12 text-center text-gray-500 bg-white rounded-xl shadow-sm">Aucun dépôt de comptes trouvé</div>
            ) : (
              filings.map((filing) => (
                <div key={filing.id} className="bg-white rounded-xl shadow-md border border-gray-200 p-4 space-y-3 hover:shadow-lg transition-shadow">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Entreprise</span>
                    <span className="text-sm text-gray-900 font-medium break-words">{filing.companyName || "N/A"}</span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">SIREN</span>
                    <span className="text-sm text-gray-900 font-medium">{fmtSiren(filing.siren)}</span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Date de clôture</span>
                    <span className="text-sm text-gray-900 font-medium">{formatDate(filing.closingDate)}</span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Statut</span>
                    <div>
                      <Chip label={getStatusLabel(filing.status)} color={getStatusColor(filing.status)} size="small" sx={{ fontSize: "0.75rem" }} />
                      {filing.rejectionReason && (
                        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5, fontSize: "0.75rem" }}>
                          {filing.rejectionReason}
                        </Typography>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Montant</span>
                    <span className="text-sm text-gray-900 font-medium">{fmtMoney(filing.amountPaid)}</span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Date de soumission</span>
                    <span className="text-sm text-gray-900 font-medium">{formatDateTime(filing.submittedAt) || "N/A"}</span>
                  </div>
                  <div className="pt-3 mt-2 border-t border-gray-200 flex flex-col gap-2">
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleViewDetails(filing.id)}
                      sx={greenOutlined}
                    >
                      Voir les détails
                    </Button>
                    {filing.status === "rejected" && (
                      <Button variant="contained" size="small" fullWidth color="error" startIcon={<EditIcon />} onClick={() => handleEditRejectedFiling(filing.id)}>
                        Modifier
                      </Button>
                    )}
                    {filing.status === "submitted" && (
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => askDeleteFiling(filing.id)}
                        disabled={isBusy}
                      >
                        Supprimer
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )
          }

          {/* Mobile Pagination (Dépôts only) */}
          <div className="mt-6 flex justify-center">
            <TablePagination
              component="div"
              count={totalFilings}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage="Par page:"
              sx={{
                ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": { fontSize: "0.75rem" },
                ".MuiTablePagination-select": { fontSize: "0.75rem" },
                ".MuiTablePagination-toolbar": { minHeight: "48px", paddingLeft: "8px", paddingRight: "8px" },
              }}
            />
          </div>
        </div>
        )}
      </div>

      {/* Single reusable AlertDialog for single/bulk/all delete flows */}
      <Dialog
        open={confirm.open}
        onClose={closeConfirm}
        disableEscapeKeyDown={isBusy}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">{confirmCopy.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmCopy.body}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm} disabled={isBusy} sx={greenText}>Annuler</Button>
          <Button
            onClick={performDelete}
            color="error"
            variant="contained"
            disabled={isBusy}
            startIcon={isBusy ? <CircularProgress size={14} sx={{ color: "white" }} /> : <DeleteIcon />}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <MuiAlert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </MuiAlert>
      </Snackbar>
    </Layout>
  );
}
